import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiValidationService } from '@rd/core/services/api-validation/api-validation.service';
import { NydusNetworkApiDataService } from '@rd/core/services/nydus-network-api-data/nydus-network-api-data.service';
import {
  createMembershipResponseSchema,
  linkMembershipResponseSchema,
  membershipResponseSchema,
  redeemCashMembershipResponseSchema,
  unlinkMembershipResponseSchema,
  updateMembershipResponseSchema,
} from '@rd/schemas/membership.schema';
import { LoyaltyProgramCategory } from '@rd/types';
import {
  CashBackMembershipModel,
  MembershipModel,
} from '@rd/types/membership.model';
import {
  CreateMembershipResponse,
  LinkMembershipResponse,
  MembershipResponse,
  RedeemCashMembershipResponse,
  UnlinkMembershipResponse,
  UpdateMembershipResponse,
} from '@rd/types/membership.response';
import {
  CashBackMembership,
  Membership,
  MembershipType,
} from '@rd/types/membership.type';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export interface CashBackToPlatformMembershipRequest {
  number: string;
  firstName: string;
  lastName: string;
}

export interface CreatePointsTransferMembershipData {
  type: MembershipType.PointsTransfer;
  loyaltyProgramShortName?: string;
  membership:
    | {
        loyalty_program_id: Membership['loyaltyProgramId'];
        number: Required<Membership['number']>;
      }
    | {
        loyalty_program_id: Membership['loyaltyProgramId'];
        number: Required<Membership['number']>;
        firstName: string;
        lastName: string;
        details: {
          dateOfBirth: string | undefined;
        };
      };
}

export interface CreateCashBackMembershipData
  extends Omit<CreatePointsTransferMembershipData, 'type' | 'membership'> {
  type: MembershipType.CashBack;
  membership: // Cashback to Card
  | {
        loyalty_program_id: string;
        enrollmentId: string;
        number: string;
        firstName?: string;
        lastName?: string;
      }
    // Cashback to account
    | {
        loyalty_program_id: string;
        number: string;
        firstName: string;
        lastName: string;
        details: {
          bsb_number: string;
        };
      }
    | CashBackToPlatformMembershipRequest;
}

export interface UpdatePointsTransferMembershipData {
  type: MembershipType.PointsTransfer;
  membershipId: string;
  loyaltyProgramShortName?: string;
  loyaltyProgramName?: string;
  membership:
    | {
        number: string;
      }
    | {
        number: string;
        firstName: string;
        lastName: string;
        details: {
          dateOfBirth: string | undefined;
        };
      };
}

export interface UpdateCashBackMembershipData
  extends Omit<UpdatePointsTransferMembershipData, 'type' | 'membership'> {
  type: MembershipType.CashBack;
  membership:
    | {
        number: string;
        enrollmentId: string;
        firstName?: string;
        lastName?: string;
      }
    | {
        number: string;
        firstName: string;
        lastName: string;
        details?: {
          bsb_number: string;
        };
      }
    | CashBackToPlatformMembershipRequest;
}

@Injectable({
  providedIn: 'root',
})
export class MembershipApiService {
  private nydusNetworkApiDataService = inject(NydusNetworkApiDataService);
  private apiValidationService = inject(ApiValidationService);

  getAllMemberships(): Observable<Membership[]> {
    return this.nydusNetworkApiDataService
      .get<MembershipResponse>('memberships')
      .pipe(
        tap((membershipResponse) => {
          this.apiValidationService.validate<MembershipResponse>({
            apiName: 'memberships',
            schema: membershipResponseSchema,
            response: membershipResponse,
          });
        }),
        map((membershipResponse) => {
          return this.transformMembershipsResponse(membershipResponse).filter(
            Boolean,
          );
        }),
      );
  }

  getRedeemCashMemberships(): Observable<CashBackMembership[]> {
    const cashBackMembershipParams = this.buildCashBackMembershipParams();

    return this.nydusNetworkApiDataService
      .get<RedeemCashMembershipResponse>(
        'memberships',
        cashBackMembershipParams,
      )
      .pipe(
        tap((membershipResponse) => {
          this.apiValidationService.validate<RedeemCashMembershipResponse>({
            apiName: `memberships?${decodeURIComponent(
              cashBackMembershipParams.toString(),
            )}`,
            schema: redeemCashMembershipResponseSchema,
            response: membershipResponse,
          });
        }),
        map((membershipResponse) => {
          return this.transformRedeemCashMembershipsResponse(
            membershipResponse,
          ).filter(Boolean);
        }),
      );
  }

  // It is possible that this can fail on a firstName/lastName required error
  // (for CreateCashBackMembershipData). Need to set the loyalty program's `name_required` property
  // to false in the DB/via AP.
  createMembership(
    createMembership:
      | CreatePointsTransferMembershipData
      | CreateCashBackMembershipData,
  ): Observable<Membership> {
    return this.nydusNetworkApiDataService
      .post<CreateMembershipResponse>('memberships', {
        membership: createMembership.membership,
      })
      .pipe(
        tap((response) => {
          this.apiValidationService.validate<CreateMembershipResponse>({
            apiName: '(POST) memberships',
            schema: createMembershipResponseSchema,
            response: response,
          });
        }),
        map((response: CreateMembershipResponse) =>
          this.transformMembershipModel(response.data),
        ),
      );
  }

  updateMembership(
    updateMembership:
      | UpdatePointsTransferMembershipData
      | UpdateCashBackMembershipData,
  ): Observable<Membership> {
    return this.nydusNetworkApiDataService
      .put<UpdateMembershipResponse>(
        `memberships/${updateMembership.membershipId}`,
        { membership: updateMembership.membership },
      )
      .pipe(
        tap((response) => {
          this.apiValidationService.validate<UpdateMembershipResponse>({
            apiName: `(PUT) memberships/${updateMembership.membershipId}`,
            schema: updateMembershipResponseSchema,
            response: response,
          });
        }),
        map((response: UpdateMembershipResponse) =>
          this.transformMembershipModel(response.data),
        ),
      );
  }

  /**
   * Link and fetch membership from external source (Currently only used for Everyday Spend)
   * @param loyaltyProgramId
   * @returns URL to redirect to for linking membership
   */
  linkMembership(loyaltyProgramId: string): Observable<LinkMembershipResponse> {
    return this.nydusNetworkApiDataService
      .get<LinkMembershipResponse>(`loyalty_programs/${loyaltyProgramId}/link`)
      .pipe(
        tap((response) => {
          this.apiValidationService.validate<LinkMembershipResponse>({
            apiName: `(GET) loyalty_programs/${loyaltyProgramId}/link`,
            schema: linkMembershipResponseSchema,
            response: response,
          });
        }),
      );
  }

  /**
   * Unlink membership from external source (Currently only used for Everyday Spend)
   * @param loyaltyProgramId
   */
  unlinkMembership(
    loyaltyProgramId: string,
  ): Observable<UnlinkMembershipResponse> {
    return this.nydusNetworkApiDataService
      .get<UnlinkMembershipResponse>(
        `loyalty_programs/${loyaltyProgramId}/unlink`,
      )
      .pipe(
        tap((response) => {
          this.apiValidationService.validate<UnlinkMembershipResponse>({
            apiName: `(GET) loyalty_programs/${loyaltyProgramId}/unlink`,
            schema: unlinkMembershipResponseSchema,
            response: response,
          });
        }),
      );
  }

  private transformMembershipsResponse(
    response: MembershipResponse,
  ): Array<Membership> {
    return response.data.map(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (membershipModel) => this.transformMembershipModel(membershipModel)!,
    );
  }

  private transformRedeemCashMembershipsResponse(
    loyaltyProgramResponse: RedeemCashMembershipResponse,
  ): CashBackMembership[] {
    return loyaltyProgramResponse.data.map(
      (membershipModel) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.transformCashBackMembershipModel(membershipModel)!,
    );
  }

  private transformMembershipModel(
    membershipModel: MembershipModel,
    // @ts-ignore
  ): Membership {
    const { id, type, attributes } = membershipModel;

    if (
      type === MembershipType.PointsTransfer &&
      (attributes.category === LoyaltyProgramCategory.Hotel ||
        attributes.category === LoyaltyProgramCategory.Airline ||
        attributes.category === LoyaltyProgramCategory.Voucher ||
        attributes.category === LoyaltyProgramCategory.Retail ||
        attributes.category === LoyaltyProgramCategory.Others)
    ) {
      return {
        id,
        category: attributes.category,
        type,
        firstName: attributes.firstName,
        lastName: attributes.lastName,
        loyaltyProgramId: attributes.loyaltyProgramId,
        loyaltyProgramName: attributes.loyaltyProgramName,
        number: attributes.number,
        preferred: attributes.preferred,
        userId: attributes.userId,
        dateOfBirth: attributes.dateOfBirth,
      };
    }

    if (
      membershipModel.type === MembershipType.CashBack &&
      (attributes.category === LoyaltyProgramCategory.CashbackToCard ||
        attributes.category === LoyaltyProgramCategory.CashbackToAccount ||
        attributes.category === LoyaltyProgramCategory.CashbackToPlatform)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.transformCashBackMembershipModel(membershipModel)!;
    }
  }

  private transformCashBackMembershipModel(
    membershipModel: CashBackMembershipModel,
    // @ts-ignore
  ): CashBackMembership {
    const { id, type, attributes } = membershipModel;

    if (type === MembershipType.CashBack) {
      switch (attributes.category) {
        case LoyaltyProgramCategory.CashbackToCard: {
          return {
            id,
            type,
            category: attributes.category,
            userId: attributes.userId,
            loyaltyProgramId: attributes.loyaltyProgramId,
            loyaltyProgramName: attributes.loyaltyProgramName,
            number: attributes.number,
            firstName: attributes.firstName,
            lastName: attributes.lastName,
            fullName: null,
            preferred: attributes.preferred,
            enrollmentId: attributes.enrollmentId,
            enrollment: {
              ...(!!attributes.enrollment && {
                id: attributes.enrollment.id,
                groupName: attributes.enrollment.groupName,
                status: attributes.enrollment.status,
              }),
            },
            details: {
              groupName: attributes.details.groupName,
            },
          };
        }
        case LoyaltyProgramCategory.CashbackToAccount: {
          return {
            id,
            type,
            category: attributes.category,
            loyaltyProgramId: attributes.loyaltyProgramId,
            loyaltyProgramName: attributes.loyaltyProgramName,
            userId: attributes.userId,
            enrollmentId: attributes.enrollmentId,
            enrollment: attributes.enrollment,
            number: attributes.number,
            firstName: attributes.firstName,
            lastName: attributes.lastName,
            fullName: null,
            details: {
              bsbNumber: attributes.details.bsbNumber,
            },
            preferred: attributes.preferred,
          };
        }
        case LoyaltyProgramCategory.CashbackToPlatform: {
          return {
            id,
            type,
            category: attributes.category,
            loyaltyProgramId: attributes.loyaltyProgramId,
            loyaltyProgramName: attributes.loyaltyProgramName,
            userId: attributes.userId,
            enrollmentId: attributes.enrollmentId,
            enrollment: attributes.enrollment,
            number: attributes.number,
            firstName: attributes.firstName,
            lastName: attributes.lastName,
            fullName: null,
            preferred: attributes.preferred,
          };
        }
      }
    }
  }

  private buildCashBackMembershipParams(): HttpParams {
    const params = new HttpParams()
      .set('sort', 'transfer_connect_identifier')
      .set('filter[type][$eq]', 'CashbackMembership');

    return params;
  }
}
